// src/AudioSpectrum/effects/aquarium.js
import { getHSLColor } from '../colorUtils.js';

// 물고기 크기 결정 함수
const getFishSize = (type, intensity) => {
  const baseSize = {
    'small': 8,
    'medium': 12,
    'large': 18,
    'shark': 25
  };
  
  const intensityMultiplier = Math.max(0.8, Math.min(2.0, intensity / 100));
  return baseSize[type] * intensityMultiplier;
};

// 물고기 색상 결정 함수
const getFishColor = (colorType, intensity) => {
  const colors = {
    'tropical': [
      getHSLColor(300 + intensity % 60, 80, 60),
      getHSLColor(180 + intensity % 40, 90, 50),
      getHSLColor(45 + intensity % 30, 85, 65)
    ],
    'goldfish': [
      getHSLColor(30, 90, 65),
      getHSLColor(15, 85, 70),
    ],
    'angelfish': [
      getHSLColor(240, 60, 70),
      getHSLColor(0, 0, 80),
    ],
    'predator': [
      getHSLColor(200, 30, 45),
      getHSLColor(0, 0, 50),
    ]
  };
  
  const colorSet = colors[colorType];
  return colorSet[Math.floor(intensity / 50) % colorSet.length];
};

// 개별 물고기 그리기 함수
const drawFish = (ctx, fish, size, intensity, time) => {
  ctx.save();
  ctx.translate(fish.x, fish.y);
  
  ctx.globalAlpha = Math.max(0.7, intensity / 150);
  
  // 물고기 몸체
  ctx.fillStyle = fish.color;
  ctx.beginPath();
  ctx.ellipse(0, 0, size, size * 0.6, 0, 0, Math.PI * 2);
  ctx.fill();
  
  // 물고기 타입별 세부 그리기
  switch (fish.type) {
    case 'small':
      drawTropicalFish(ctx, size, fish, time);
      break;
    case 'medium':
      drawGoldfish(ctx, size, fish, time);
      break;
    case 'large':
      drawAngelfish(ctx, size, fish, time);
      break;
    case 'shark':
      drawShark(ctx, size, fish, time);
      break;
  }
  
  ctx.restore();
};

// 열대어 그리기
const drawTropicalFish = (ctx, size, fish, time) => {
  // 몸체 줄무늬
  ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.ellipse(-size * 0.3 + i * size * 0.2, 0, size * 0.1, size * 0.5, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  
  // 꼬리 지느러미 (흔들림)
  const tailSway = Math.sin(fish.tailPhase) * 0.3;
  ctx.fillStyle = fish.color;
  ctx.beginPath();
  ctx.moveTo(-size, 0);
  ctx.lineTo(-size * 1.5, -size * 0.4 + tailSway);
  ctx.lineTo(-size * 1.3, 0);
  ctx.lineTo(-size * 1.5, size * 0.4 + tailSway);
  ctx.closePath();
  ctx.fill();
  
  // 눈
  ctx.fillStyle = 'white';
  ctx.beginPath();
  ctx.arc(size * 0.3, -size * 0.2, size * 0.15, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'black';
  ctx.beginPath();
  ctx.arc(size * 0.35, -size * 0.2, size * 0.08, 0, Math.PI * 2);
  ctx.fill();
};

// 금붕어 그리기
const drawGoldfish = (ctx, size, fish, time) => {
  // 등 지느러미
  const finSway = Math.sin(fish.finPhase) * 0.2;
  ctx.fillStyle = fish.color;
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.6);
  ctx.lineTo(-size * 0.3, -size * 1.2 + finSway);
  ctx.lineTo(size * 0.3, -size * 1.2 + finSway);
  ctx.closePath();
  ctx.fill();
  
  // 꼬리 (부채꼴)
  const tailSway = Math.sin(fish.tailPhase) * 0.4;
  ctx.beginPath();
  ctx.moveTo(-size, 0);
  ctx.lineTo(-size * 1.8, -size * 0.6 + tailSway);
  ctx.lineTo(-size * 1.6, 0);
  ctx.lineTo(-size * 1.8, size * 0.6 + tailSway);
  ctx.closePath();
  ctx.fill();
  
  // 눈
  ctx.fillStyle = 'white';
  ctx.beginPath();
  ctx.arc(size * 0.4, -size * 0.1, size * 0.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'black';
  ctx.beginPath();
  ctx.arc(size * 0.45, -size * 0.1, size * 0.1, 0, Math.PI * 2);
  ctx.fill();
};

// 엔젤피쉬 그리기
const drawAngelfish = (ctx, size, fish, time) => {
  // 긴 지느러미들
  const finSway = Math.sin(fish.finPhase) * 0.3;
  ctx.fillStyle = fish.color;
  
  // 위 지느러미
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.6);
  ctx.lineTo(-size * 0.2, -size * 2 + finSway);
  ctx.lineTo(size * 0.2, -size * 2 + finSway);
  ctx.closePath();
  ctx.fill();
  
  // 아래 지느러미
  ctx.beginPath();
  ctx.moveTo(0, size * 0.6);
  ctx.lineTo(-size * 0.2, size * 2 - finSway);
  ctx.lineTo(size * 0.2, size * 2 - finSway);
  ctx.closePath();
  ctx.fill();
  
  // 꼬리
  const tailSway = Math.sin(fish.tailPhase) * 0.2;
  ctx.beginPath();
  ctx.moveTo(-size, 0);
  ctx.lineTo(-size * 1.4, -size * 0.8 + tailSway);
  ctx.lineTo(-size * 1.2, 0);
  ctx.lineTo(-size * 1.4, size * 0.8 + tailSway);
  ctx.closePath();
  ctx.fill();
  
  // 눈
  ctx.fillStyle = 'white';
  ctx.beginPath();
  ctx.arc(size * 0.3, 0, size * 0.18, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'black';
  ctx.beginPath();
  ctx.arc(size * 0.35, 0, size * 0.09, 0, Math.PI * 2);
  ctx.fill();
};

// 상어 그리기
const drawShark = (ctx, size, fish, time) => {
  // 더 날카로운 몸체로 다시 그리기
  ctx.fillStyle = fish.color;
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(0, -size * 0.4);
  ctx.lineTo(-size * 1.2, -size * 0.2);
  ctx.lineTo(-size * 1.2, size * 0.2);
  ctx.lineTo(0, size * 0.4);
  ctx.closePath();
  ctx.fill();
  
  // 등 지느러미 (삼각형)
  ctx.beginPath();
  ctx.moveTo(-size * 0.2, -size * 0.4);
  ctx.lineTo(-size * 0.1, -size * 1.2);
  ctx.lineTo(size * 0.2, -size * 0.4);
  ctx.closePath();
  ctx.fill();
  
  // 꼬리 지느러미 (강력한 추진력)
  const tailSway = Math.sin(fish.tailPhase) * 0.1;
  ctx.beginPath();
  ctx.moveTo(-size * 1.2, 0);
  ctx.lineTo(-size * 2, -size * 0.8 + tailSway);
  ctx.lineTo(-size * 1.8, 0);
  ctx.lineTo(-size * 2, size * 0.4 + tailSway);
  ctx.closePath();
  ctx.fill();
  
  // 눈 (작고 날카로운)
  ctx.fillStyle = 'red';
  ctx.beginPath();
  ctx.arc(size * 0.4, -size * 0.1, size * 0.12, 0, Math.PI * 2);
  ctx.fill();
};

// 해초 그리기
const drawSeaweed = (ctx, width, height, time) => {
  const seaweedCount = 5;
  for (let i = 0; i < seaweedCount; i++) {
    const x = (i + 1) * (width / (seaweedCount + 1));
    const seaweedHeight = height * 0.6 + Math.sin(time * 0.001 + i) * 50;
    
    ctx.strokeStyle = getHSLColor(120 + i * 10, 60, 40);
    ctx.lineWidth = 3 + Math.sin(time * 0.002 + i) * 1;
    
    ctx.beginPath();
    ctx.moveTo(x, height);
    
    const segments = 10;
    for (let j = 1; j <= segments; j++) {
      const segmentY = height - (j / segments) * seaweedHeight;
      const sway = Math.sin(time * 0.003 + i + j * 0.5) * (j * 2);
      ctx.lineTo(x + sway, segmentY);
    }
    
    ctx.stroke();
  }
};

export const drawAquarium = (ctx, frequencyData, width, height, fishRef, bubblesRef) => {
  // 수중 배경 그라디언트
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, 'rgba(20, 50, 100, 0.1)');
  gradient.addColorStop(0.5, 'rgba(10, 80, 120, 0.05)');
  gradient.addColorStop(1, 'rgba(5, 60, 80, 0.08)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  
  const time = Date.now();
  
  // 주파수를 여러 대역으로 나누어 다른 종류의 물고기 배치
  const frequencyBands = [
    { range: [0, 20], fishType: 'small', color: 'tropical', count: 8 },
    { range: [20, 60], fishType: 'medium', color: 'goldfish', count: 6 },
    { range: [60, 120], fishType: 'large', color: 'angelfish', count: 4 },
    { range: [120, 200], fishType: 'shark', color: 'predator', count: 2 }
  ];
  
  frequencyBands.forEach((band, bandIndex) => {
    const startIdx = Math.floor((band.range[0] / 256) * frequencyData.length);
    const endIdx = Math.floor((band.range[1] / 256) * frequencyData.length);
    const bandData = frequencyData.slice(startIdx, endIdx);
    const avgIntensity = bandData.reduce((sum, val) => sum + val, 0) / bandData.length;
    
    // 기존 물고기 필터링 (해당 밴드만)
    const existingFish = fishRef.current.filter(fish => fish.band === bandIndex);
    
    // 새로운 물고기 생성 (intensity가 높을 때)
    if (avgIntensity > 30 && existingFish.length < band.count) {
      const newFishCount = Math.min(
        Math.floor(avgIntensity / 50), 
        band.count - existingFish.length
      );
      
      for (let i = 0; i < newFishCount; i++) {
        fishRef.current.push({
          id: time + bandIndex * 1000 + i,
          band: bandIndex,
          x: -50,
          y: (height / 4) * (bandIndex + 1) + (Math.random() - 0.5) * 100,
          vx: 0.5 + Math.random() * 1.5,
          vy: 0,
          size: getFishSize(band.fishType, avgIntensity),
          type: band.fishType,
          color: getFishColor(band.color, avgIntensity),
          tailPhase: Math.random() * Math.PI * 2,
          finPhase: Math.random() * Math.PI * 2,
          life: 1.0,
          targetY: (height / 4) * (bandIndex + 1),
          swayAmount: Math.random() * 30 + 20,
          birthTime: time,
          lastIntensity: avgIntensity
        });
      }
    }
    
    // 물방울 생성 (고주파에서)
    if (avgIntensity > 80 && Math.random() < 0.1) {
      bubblesRef.current.push({
        x: Math.random() * width,
        y: height - 20,
        vx: (Math.random() - 0.5) * 0.5,
        vy: -(1 + Math.random() * 2),
        size: Math.random() * 3 + 1,
        life: 1.0,
        decay: 0.01
      });
    }
  });
  
  // 물고기 업데이트 및 렌더링
  fishRef.current = fishRef.current.filter(fish => {
    const band = frequencyBands[fish.band];
    const startIdx = Math.floor((band.range[0] / 256) * frequencyData.length);
    const endIdx = Math.floor((band.range[1] / 256) * frequencyData.length);
    const bandData = frequencyData.slice(startIdx, endIdx);
    const currentIntensity = bandData.reduce((sum, val) => sum + val, 0) / bandData.length;
    
    // intensity에 따른 속도 조절
    const speedMultiplier = Math.max(0.3, currentIntensity / 100);
    const baseSpeed = fish.type === 'small' ? 1.2 : fish.type === 'medium' ? 1.0 : 
                    fish.type === 'large' ? 0.8 : 0.6;
    
    // 물고기 이동
    fish.vx = baseSpeed * speedMultiplier;
    fish.x += fish.vx;
    
    // 수직 움직임 (자연스러운 헤엄치기)
    const swimTime = (time - fish.birthTime) * 0.002;
    const targetOffset = Math.sin(swimTime) * fish.swayAmount;
    fish.targetY = (height / 4) * (fish.band + 1) + targetOffset;
    fish.vy = (fish.targetY - fish.y) * 0.02;
    fish.y += fish.vy;
    
    // 꼬리와 지느러미 애니메이션
    fish.tailPhase += 0.15 * speedMultiplier;
    fish.finPhase += 0.1 * speedMultiplier;
    
    // intensity 변화에 따른 크기 조절
    const sizeMultiplier = Math.max(0.7, currentIntensity / 128);
    const currentSize = getFishSize(fish.type, currentIntensity) * sizeMultiplier;
    
    if (fish.x < width + 100) {
      drawFish(ctx, fish, currentSize, currentIntensity, time);
      return true;
    }
    return false;
  });
  
  // 물방울 업데이트 및 렌더링
  bubblesRef.current = bubblesRef.current.filter(bubble => {
    bubble.x += bubble.vx;
    bubble.y += bubble.vy;
    bubble.life -= bubble.decay;
    
    if (bubble.life > 0 && bubble.y > -20) {
      ctx.globalAlpha = bubble.life;
      ctx.fillStyle = 'rgba(200, 230, 255, 0.6)';
      ctx.beginPath();
      ctx.arc(bubble.x, bubble.y, bubble.size, 0, Math.PI * 2);
      ctx.fill();
      
      // 물방울 하이라이트
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.beginPath();
      ctx.arc(bubble.x - bubble.size * 0.3, bubble.y - bubble.size * 0.3, 
              bubble.size * 0.3, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.globalAlpha = 1.0;
      return true;
    }
    return false;
  });
  
  // 해초/수초 그리기 (배경 효과)
  drawSeaweed(ctx, width, height, time);
};